import { useEffect } from 'react'
import { useServerSequence } from '@/hooks/useInfra'
import { lookupErrorMessage } from '@/services/infraLive'
import { useInfrastructureWizard } from '@/stores/infrastructureWizard'
import { resolveServerName } from '@/utils/infraValidation'
import { SERVER_ROLE_LABELS, hasNamingRule, isPlaceholderPattern, sequenceKey, serverNameSeries, type ServerRole } from '@/utils/naming'
import page from '@/pages/NewDeploymentPage.module.css'
import styles from './Infrastructure.module.css'
import { ReadOnlyInfoCard } from './ReadOnlyInfoCard'
import { TextField } from './TextField'

interface ServerNameCardProps {
  role: ServerRole
  /** 'azure' is for an Azure SQL (PaaS) server, whose name Azure requires in lower case. */
  style?: 'host' | 'azure'
  title?: string
  idPrefix: string
  /** How many consecutive names the role needs: the RDS VMs of the RDS server, all shown. The first is the server's name. */
  count?: number
}

/**
 * A server's generated name, always shown read-only with a note on how its number was chosen ("Next number after PMTTRAVRDS38."). It is a
 * PREVIEW, and the note says the final name is confirmed when the deployment is created: the pipeline reserves the real one just before
 * Terraform runs. The number comes from `getNextServerSequence` (one more than the highest Azure VM name of the series in the
 * environment's subscription), which is kept in the store for the validators and the payload. A product with no naming rule types its SQL
 * server name instead: the only time a name is an input.
 */
export function ServerNameCard({ role, style = 'host', title, idPrefix, count = 1 }: ServerNameCardProps) {
  const product = useInfrastructureWizard((s) => s.product)
  const environment = useInfrastructureWizard((s) => s.environment)
  const serverSequences = useInfrastructureWizard((s) => s.serverSequences)
  const manualSqlServerName = useInfrastructureWizard((s) => s.manualSqlServerName)
  const setManual = useInfrastructureWizard((s) => s.setManualSqlServerName)
  const setServerSequence = useInfrastructureWizard((s) => s.setServerSequence)
  const environmentId = environment?.id ?? ''
  const named = hasNamingRule(product)
  const series = named ? serverNameSeries({ product, environmentId, role }) : null
  const seriesParts = series?.ok ? series.series : null
  const sequence = useServerSequence(environmentId, product, role, seriesParts !== null)
  const key = seriesParts ? sequenceKey(environmentId, seriesParts) : ''

  // Keep the number in the store, where the validators and the payload read the name.
  useEffect(() => {
    if (key && sequence.data) setServerSequence(key, sequence.data)
  }, [key, sequence.data, setServerSequence])

  const heading = title ?? `New ${SERVER_ROLE_LABELS[role]} name`
  // Until the effect above has copied a fetched number into the store, use it straight from the query, so the card does not flash
  // a "Working out" message in between. The store wins once it has one (the recheck at submit writes both).
  const sequences = key && sequence.data ? { [key]: sequence.data, ...serverSequences } : serverSequences
  const resolved = resolveServerName({ product, environment, serverSequences: sequences, manualSqlServerName }, role, style, count)

  if (!named) {
    return (
      <div className={styles.section} style={{ borderTop: 'none', paddingTop: 0 }}>
        <TextField
          id={`${idPrefix}-name`}
          label="SQL server name"
          required
          value={manualSqlServerName}
          onChange={setManual}
          maxLength={15}
          hint="No naming rule is configured for this product, so the name has to be entered. Up to 15 letters, digits or hyphens."
          problem={manualSqlServerName.trim() !== '' && !resolved.ok ? resolved.problem : null}
        />
        {resolved.ok && <ReadOnlyInfoCard title={heading} items={[{ label: 'Name', value: resolved.name, mono: true }]} note={resolved.note} />}
      </div>
    )
  }

  if (series && !series.ok) {
    return (
      <div role="alert" className={page.lbInfoBanner}>
        <strong>{heading}:</strong> {series.problem}
      </div>
    )
  }

  if (sequence.isLoading || sequence.isError) {
    return (
      <ReadOnlyInfoCard
        title={heading}
        loading={sequence.isLoading}
        error={sequence.isError ? (lookupErrorMessage(sequence.error) ?? 'Could not work out the next free number for this name.') : null}
        onRetry={() => void sequence.refetch()}
        items={[]}
      />
    )
  }

  if (!resolved.ok) {
    return (
      <div role="alert" className={page.lbInfoBanner}>
        <strong>{heading}:</strong> {resolved.problem}
      </div>
    )
  }

  const placeholder = isPlaceholderPattern(product, role) ? ' This pattern is a placeholder until it is confirmed.' : ''
  const names = resolved.names ?? [resolved.name]
  return (
    <ReadOnlyInfoCard
      title={heading}
      items={[{ label: names.length > 1 ? 'Names' : 'Name', value: names.join(', '), mono: true }]}
      note={`${resolved.note}${placeholder}`}
    />
  )
}
